// import { useSelector } from "react-redux"
import styles from "./styles/index.module.scss";
import { ChevronDown, ChevronRight } from "react-feather";
import { useTranslation } from "react-i18next";
import {} from "reactstrap";
import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { UserContext } from "../../Context/UserContext";
import { encryptData } from "../../Helper/EssentilaMethods/Encryption";
import { decryptData } from "../../Helper/EssentilaMethods/Decryption";
import statuses from "./files/thunesTransactionStatus.json";
import TransactionItemDetails from "./TransactionItemDetails.jsx";
import CitiesData from "../CitiesDropDown/CitiesData.json";
import { formatCurrency } from "../../Helper/AmountFormat.js";
import { usePermission } from "../../hooks/GeneralUse/usePermission";

export const service_name_formatter = (itemName) => {
  if (itemName) {
    const nameArr = itemName.split("_");
    const newName = nameArr
      .map((item) =>
        item?.toLowerCase() === "aba" ? "ABA" : `${item[0].toUpperCase()}${item?.slice(1)}`,
      )
      .join(" ");
    return newName;
  }
};

const TransactionItem = ({
  item,
  activeItem,
  setActiveItem,
  index,
  fromReferral = false,
  vvc = false,
  cardNo,
}) => {
  const { t, i18n } = useTranslation();
  const can = usePermission();
  const [selectedCategory, setSelectedCategory] = useState([]);
  const { authHeader, beneficiaries } = useContext(UserContext);
  const [note, setNote] = useState(item?.note);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [message, setMessage] = useState("");
  const [symbol, setSymbol] = useState("");

  useEffect(() => {
    setNote(item?.note);
    setIsEditMode(item?.note ? false : true);
  }, [item]);

  useEffect(() => {
    if (vvc) {
      const data = CitiesData.find((d) => d.currency === item.currency);
      setSymbol(data.currency_symbol);
    }
  }, [item]);

  const badgeColor = useMemo(() => {
    switch (item?.status?.toLowerCase()) {
      case "initiated":
        return "#ADD8E6";
      case "completed":
        return "#28A745";
      case "success":
        return "#28A745";
      case "rejected":
        return "#DC3545";
      case "cancelled":
        return "#C82333";
      case "submitted":
        return "#FFA500";
      case "pending":
        return "#FFA500";
      case "available":
        return "#007BFF";
      case "confirmed":
        return "#218838";
      case "reversed":
        return "#FFC107";
      case "declined":
      case "failed":
        return "#C82333";
      case "processing":
        return "#ADD8E6";
      case "refunded":
        return "#9f83f2";
      case "reverted":
        return "#9f83f2";
      case "abandoned":
        return "#FFA500";
      default:
        break;
    }
  }, [item]);
  const addCategoriesToTransaction = () => {
    setMessage("");
    setIsSaving(true);
    const data = {
      transaction_id: item._id,
      categories: selectedCategory.map((item) => item._id),
      note,
    };
    // console.log(data)
    const encrypted = encryptData(data);
    axios
      .post(
        `${import.meta.env.VITE_IP_DEV_API}/api/transaction/add-transactions-category`,
        {
          data: encrypted,
        },
        {
          headers: authHeader,
        },
      )
      .then((res) => {
        void decryptData(res.data.data);
        // console.log(decrypted)
        setIsSaving(false);
        setIsEditMode(false);
        setMessage("Categories Added Successfully!");
        setTimeout(() => {
          setMessage("");
        }, 3000);
      })
      .catch((err) => {
        void decryptData(err.response.data.data);
        // console.log(decrypted)
        setIsSaving(false);
        setMessage("Something went wrong, try again.");
        setTimeout(() => {
          setMessage("");
        }, 3000);
      });
  };

  const internationalReceiver = useMemo(() => {
    if (!item?.beneficiary) return null;
    return (
      beneficiaries.filter((benef) => benef?._id === item?.beneficiary)?.[0] ||
      item?.beneficiaryDetails
    );
  }, [beneficiaries, item]);

  const externalStatusDescription = useMemo(() => {
    if (item?.payment_type === "non_instapay") {
      return item?.description || undefined;
    }
    return statuses?.[item?.external_status?.split("_")?.[1]]?.[i18n.language]?.message ?? "";
  }, [item, i18n]);

  function capitalizeStatus(str) {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
  const addDecimals = (value) => {
    const splitted = value.toString().split(".");

    if (splitted.length >= 2) {
      if (splitted[1][0].length >= 2) {
        return value;
      } else if (splitted[1][0].length === 1) {
        return `${value}0`;
      } else {
        return `${value}00`;
      }
    } else {
      return `${value}.00`;
    }
  };

  const displayAsText = useMemo(() => {
    if (item?.service_type === "withdraw_by_admin" || item?.service_type === "deposite_by_admin") {
      return "Admin Funds";
    }
    if (item?.payment_type?.includes("bank")) {
      return "Bank Transfer";
    }
    if (item?.payment_type?.includes("mobile")) {
      return "Mobile Money";
    }
    return service_name_formatter(item?.payment_type);
  }, [item]);

  const displayName = useMemo(() => {
    if (item?.guest_details?.name && item?.guest_details) {
      return item?.guest_details?.name ?? "";
    }

    if (item?.service_type === "airtime") {
      return item?.airtime_number ?? "";
    }

    if (item?.payment_type === "conversion") {
      return item?.wallet_id ?? "";
    }

    const isDebit = (item?.transaction_type ?? "")?.toLowerCase() === "debit";
    if (isDebit) {
      if (item?.service_type === "withdraw_by_admin") return "Admin";
      if (item?.service_type === "kyc_verification") return "InstaPay";
      if (item?.service_type === "withdrawal") {
        const first = item?.sender?.user?.first_name ?? item?.sender?.company?.company_name;
        const last = item?.sender?.user?.last_name ?? "";
        return [first, last].filter(Boolean).join(" ");
      }
      if (item?.receiver?.user) {
        const first = item?.receiver?.user?.first_name ?? item?.receiver?.company?.company_name;
        const last = item?.receiver?.user?.last_name ?? "";
        return [first, last].filter(Boolean).join(" ");
      }

      const first =
        internationalReceiver?.first_name ??
        item?.account?.user?.first_name ??
        internationalReceiver?.company_name ??
        item?.account?.company?.company_name;
      const last = internationalReceiver?.last_name ?? item?.account?.user?.last_name ?? "";
      return [first, last].filter(Boolean).join(" ");
    }

    if (item?.service_type === "topup") {
      const first = item?.receiver?.user?.first_name;
      const last = item?.receiver?.user?.last_name;
      return [first, last].filter(Boolean).join(" ");
    }

    const first =
      item?.sender?.user?.first_name ??
      item?.mastercard?.account?.first_name ??
      item?.sender?.company?.company_name;
    const last = item?.sender?.user?.last_name ?? item?.mastercard?.account?.last_name ?? "";
    return [first, last].filter(Boolean).join(" ");
  }, [item, internationalReceiver]);

  return (
    <div
      style={
        item?.third_party_sender?._id
          ? {
              borderLeft: "4px solid #6228e5",
            }
          : {}
      }
    >
      <div
        className={styles.body_item}
        onClick={() => {
          // if(vvc) return
          if (!can("transactions.main.details.view")) return;
          if (!activeItem || activeItem !== index + 1) {
            setActiveItem(index + 1);
          } else {
            setActiveItem(0);
          }
        }}
      >
        <div style={{ minWidth: "155px", whiteSpace: "nowrap" }}>
          {new Date(item?.createdAt)?.toLocaleString()}
        </div>
        {vvc && <div>{item?.transactionId}</div>}
        {vvc && (
          <div>
            {t("••")}
            {cardNo?.slice(-4)}
          </div>
        )}
        {vvc && <div>{item?.type}</div>}
        {vvc && (
          <div>
            {service_name_formatter(item?.transaction_type)?.replace("Mastercard", "") ?? "--"}{" "}
          </div>
        )}

        {!vvc && (
          <>
            <div>
              {item?.service_type === "topup" && "Wallet"}{" "}
              {service_name_formatter(t(item?.service_type))}
            </div>
            <div>{item?.reference_id}</div>
            <div>{t(displayAsText)}</div>
            <div>{displayName?.slice(0, 20)}</div>
            <div
              style={{
                color: item?.transaction_type?.toLowerCase() === "debit" ? "#FF7293" : "#1CB234",
              }}
            >
              {item?.transaction_type?.toLowerCase() === "debit" ? "-" : "+"}
              {item?.payment_type?.toLowerCase() === "commission" && fromReferral
                ? "$"
                : item.service_type === "topup" && item.payment_type === "bank"
                  ? item.recipient_received_currency
                  : item?.currency?.code}{" "}
              {item?.payment_type?.toLowerCase() === "commission" && fromReferral
                ? item?.replacement_currency?.value?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : (item.service_type === "topup" && item.payment_type === "bank"
                    ? item.recipient_received_amount
                    : item?.total
                  )?.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
            </div>
          </>
        )}

        {vvc && (
          <div style={{ color: item?.type === "credit" ? "green" : "red" }}>
            {item?.billCurrency
              ? `${item?.billCurrency} ${formatCurrency(item?.billAmount)}`
              : `${item?.currency} ${formatCurrency(item?.txAmount)}`}
            {item?.type === "credit" ? "+" : "-"}
          </div>
        )}

        <div>
          <div
            style={{
              background: badgeColor,
              fontSize: "12px",
              color: "white",
              width: "100px",
              margin: "0 auto",
              padding: "5px",
              borderRadius: "30px",
              textAlign: "center",
            }}
          >
            {t(capitalizeStatus(item?.status))}
          </div>
        </div>

        <div style={{ maxWidth: "15px" }}>
          {activeItem === index + 1 ? <ChevronDown /> : <ChevronRight />}
        </div>
      </div>
      {activeItem === index + 1 &&
        // Permission: transactions.main.details.view
        can("transactions.main.details.view") && (
          <TransactionItemDetails
            fromReferral={fromReferral}
            badgeColor={badgeColor}
            desc={externalStatusDescription}
            internationalReceiver={internationalReceiver}
            isEditMode={isEditMode}
            item={item}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            showCategories={true}
            addCategoriesToTransaction={addCategoriesToTransaction}
            isSaving={isSaving}
            message={message}
            setIsEditMode={setIsEditMode}
            setMessage={setMessage}
            note={note}
            setNote={setNote}
            cardNo={cardNo}
            vvc={vvc}
          />
        )}
    </div>
  );
};

export default TransactionItem;
